import axios from "axios";

import create from "zustand";

import { NGROK } from "../APIs";



const SYNC_POLL_INTERVAL_MS = 5000;

const MAX_POLL_MS = 1800000;

const SYNC_STATE_KEY = "cloudSyncActive";



const getStoredSyncState = () => {

  try {

    const stored = localStorage.getItem(SYNC_STATE_KEY);

    return stored ? JSON.parse(stored) : {};

  } catch (error) {

    console.error("Error reading cloud sync state from localStorage:", error);

    return {};

  }

};



const saveSyncStateToStorage = (cloud, tenantName) => {

  try {

    const stored = getStoredSyncState();

    stored[cloud] = { tenantName };

    localStorage.setItem(SYNC_STATE_KEY, JSON.stringify(stored));

  } catch (error) {

    console.error("Error saving cloud sync state to localStorage:", error);

  }

};



const clearSyncStateFromStorage = (cloud) => {

  try {

    const stored = getStoredSyncState();

    delete stored[cloud];

    localStorage.setItem(SYNC_STATE_KEY, JSON.stringify(stored));

  } catch (error) {

    console.error("Error clearing cloud sync state from localStorage:", error);

  }

};



const fetchSyncRunningStatus = async (cloud, tenantName) => {

  const response = await axios.get(`${NGROK}/api/cloud-sync/status`, {

    params: { cloudType: cloud, tenantName },

  });

  return Boolean(response.data?.running);

};



const initializeState = () => {

  const stored = getStoredSyncState();

  const syncStatus = { aws: false, azure: false, gcp: false };

  const syncPollIntervals = { aws: null, azure: null, gcp: null };

  const syncTenants = { aws: null, azure: null, gcp: null };

  const syncCallbacks = { aws: null, azure: null, gcp: null };



  Object.keys(stored).forEach((cloud) => {

    const tenantName = stored[cloud]?.tenantName;

    if (tenantName) {

      syncStatus[cloud] = true;

      syncTenants[cloud] = tenantName;

    }

  });



  return { syncStatus, syncPollIntervals, syncTenants, syncCallbacks };

};



const useCloudSyncStore = create((set, get) => ({

  ...initializeState(),



  registerSyncCallback: (cloud, callback) => {

    if (typeof callback !== "function") {

      return;

    }

    set((current) => ({

      syncCallbacks: { ...current.syncCallbacks, [cloud]: callback },

    }));

  },

  unregisterSyncCallback: (cloud, callback) => {
    const state = get();
    if (typeof callback !== "function" || state.syncCallbacks[cloud] !== callback) {
      return;
    }
    set((current) => ({
      syncCallbacks: { ...current.syncCallbacks, [cloud]: null },
    }));
  },



  setSyncStatus: (cloud, status) =>

    set((state) => ({

      syncStatus: { ...state.syncStatus, [cloud]: status },

    })),



  stopSyncPolling: (cloud) => {

    const state = get();

    if (state.syncPollIntervals[cloud]) {

      clearInterval(state.syncPollIntervals[cloud]);

    }

    set((current) => ({

      syncPollIntervals: { ...current.syncPollIntervals, [cloud]: null },

    }));

  },



  finishSync: (cloud, onComplete) => {

    const state = get();

    state.stopSyncPolling(cloud);

    state.setSyncStatus(cloud, false);

    clearSyncStateFromStorage(cloud);

    const callback = onComplete || state.syncCallbacks[cloud];

    set((current) => ({

      syncTenants: { ...current.syncTenants, [cloud]: null },

      syncCallbacks: { ...current.syncCallbacks, [cloud]: null },

    }));

    if (typeof callback === "function") {

      callback();

    }

  },



  startSyncPolling: (cloud, tenantName, onComplete) => {

    const state = get();

    if (!tenantName) {

      return;

    }



    if (typeof onComplete === "function") {

      state.registerSyncCallback(cloud, onComplete);

    }



    state.stopSyncPolling(cloud);

    state.setSyncStatus(cloud, true);

    saveSyncStateToStorage(cloud, tenantName);

    set((current) => ({

      syncTenants: { ...current.syncTenants, [cloud]: tenantName },

    }));



    const startedAt = Date.now();

    let onCompleteCalled = false;



    const poll = async () => {

      try {

        const running = await fetchSyncRunningStatus(cloud, tenantName);

        const refreshCallback = get().syncCallbacks[cloud];



        if (running) {

          if (typeof refreshCallback === "function") {

            try {

              await refreshCallback({ live: true });

            } catch (refreshError) {

              console.error(`Live refresh failed for ${cloud}:`, refreshError);

            }

          }

          if (Date.now() - startedAt > MAX_POLL_MS) {

            get().finishSync(cloud);

          }

          return;

        }



        if (!onCompleteCalled) {

          onCompleteCalled = true;

          get().finishSync(cloud);

        }

      } catch (error) {

        console.error(`Sync status poll failed for ${cloud}:`, error);

      }

    };



    poll();

    const intervalId = setInterval(poll, SYNC_POLL_INTERVAL_MS);

    set((current) => ({

      syncPollIntervals: { ...current.syncPollIntervals, [cloud]: intervalId },

    }));

  },



  resumeSyncPollingIfNeeded: async (cloud, tenantName, onComplete) => {

    if (!tenantName) {

      return;

    }



    if (typeof onComplete === "function") {

      get().registerSyncCallback(cloud, onComplete);

    }



    const state = get();

    if (state.syncPollIntervals[cloud]) {

      if (state.syncStatus[cloud]) {

        return;

      }

    }



    try {

      const running = await fetchSyncRunningStatus(cloud, tenantName);

      if (running) {

        state.startSyncPolling(cloud, tenantName, onComplete || state.syncCallbacks[cloud]);

      } else if (state.syncStatus[cloud]) {

        state.finishSync(cloud);

      } else {

        clearSyncStateFromStorage(cloud);

        set((current) => ({

          syncTenants: { ...current.syncTenants, [cloud]: null },

        }));

      }

    } catch (error) {

      console.error(`Failed to resume sync polling for ${cloud}:`, error);

    }

  },



  clearSyncTracking: (cloud) => {

    get().finishSync(cloud);

  },



  resetSyncStates: () =>

    set(() => {

      const state = get();

      Object.keys(state.syncPollIntervals).forEach((cloud) => {

        if (state.syncPollIntervals[cloud]) {

          clearInterval(state.syncPollIntervals[cloud]);

        }

        clearSyncStateFromStorage(cloud);

      });

      localStorage.removeItem(SYNC_STATE_KEY);

      return {

        syncStatus: {

          aws: false,

          azure: false,

          gcp: false,

        },

        syncPollIntervals: {

          aws: null,

          azure: null,

          gcp: null,

        },

        syncTenants: {

          aws: null,

          azure: null,

          gcp: null,

        },

        syncCallbacks: {

          aws: null,

          azure: null,

          gcp: null,

        },

      };

    }),

}));



export default useCloudSyncStore;
